/**
 * urlDetection.js — serbest metin (toplu bildirim / duyuru başlığı-mesajı)
 * içinde bir bağlantı/URL var mı, ADMİN'E GÖNDERMEDEN ÖNCE bir onay uyarısı
 * göstermek için tespit eder.
 *
 * Bağlam (security denetimi, 2026-08-04): `isSafeInternalDeeplink`
 * (bkz. deeplink.js) SADECE ayrı `deeplink` alanını korur — admin'in yazdığı
 * serbest `title`/`message` metni bu guard'dan hiç geçmez. Metne
 * "http://evil.com" yazılabilir; bazı Android launcher'ları düz metindeki
 * URL'leri otomatik linkify eder → bildirimler artık gerçek push olarak
 * cihaza gittiğinden bu somut bir phishing/kilit-ekranı riskidir.
 *
 * BU FONKSİYON BİR GÜVENLİK ENGELLEYİCİSİ DEĞİLDİR — kullanıcı kararı: uyarı
 * eklensin, ENGELLEMESİN. Admin'in bilinçli olarak (ör. destek adresi,
 * kariyeronline.tr gibi kendi domaini) bağlantı içeren mesaj göndermesi
 * gerekebilir; bu yüzden findUrlInText/hasUrlInText SADECE bir onay adımı
 * (ConfirmDialog) tetiklemek için kullanılır, gönderimi ASLA reddetmez.
 *
 * Tetikler:
 *   - "http://" veya "https://" ile başlayan her şey
 *   - "www." ile başlayanlar
 *   - TLD'li çıplak alan adları: "evil.com", "kisa.link/abc"
 *   - E-posta adresleri (ör. "destek@example.com") — bilinçli karar: bir
 *     duyuruda e-posta adresi geçiyorsa da admin bunu görüp onaylamalı,
 *     zira @ öncesi/sonrası serbest metinde yanlışlıkla farklı bir domain de
 *     yapıştırılmış olabilir. (Bu, aşağıdaki çıplak alan adı deseninin doğal
 *     bir sonucu — "destek@example.com" içindeki "example.com" parçası zaten
 *     TLD listesine uyduğu için ayrıca özel bir e-posta deseni gerekmez.)
 *
 * Tetiklemez (yanlış pozitif önleme):
 *   - Uygulama-içi path'ler: "/dashboard", "/courses/123", "/cv" — bunlarda
 *     nokta+harf içeren bir TLD ardışıklığı yok.
 *   - Cümle sonu noktaları: "Bugün dersin var. Yarın sınav." — noktadan
 *     sonra BOŞLUK geldiği için ardışık "kelime.harf" eşleşmesi oluşmaz.
 *   - Sürüm numaraları: "v1.2.3" — son bölüm ("3") rakam, TLD listesindeki
 *     hiçbir girişle eşleşmez.
 *   - Saat ifadeleri: "14.30'da" — nokta sonrası rakam ("30"), ardından
 *     kesme işareti; ne bir sonraki nokta+TLD zinciri ne de doğrudan TLD
 *     alternatifi eşleşir.
 *
 * NOT: Generic `[a-z]{2,}` yerine somut bir TLD listesi kullanılır — aksi
 * halde "Sn.Ahmet" gibi boşluksuz Türkçe unvan+isim kalıpları da "alan adı"
 * sanılabilirdi (ör. ".Ahmet" 5 harfli bir "TLD" gibi görünür).
 */

// Yaygın TLD'ler + ".tr" bileşik uzantılar. Uzundan kısaya sıralanır —
// alternatif (|) denemesi "com.tr"yi "com"dan önce dener (gösterilen eşleşme
// metni daha anlamlı olur; tespit sonucu her iki sırada da aynıdır).
const TLDS = [
  'com.tr', 'net.tr', 'org.tr', 'edu.tr', 'gov.tr', 'web.tr', 'info.tr',
  'biz.tr', 'name.tr', 'av.tr', 'bel.tr', 'pol.tr', 'k12.tr', 'tsk.tr', 'kep.tr',
  'com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'info', 'biz', 'name', 'pro',
  'co', 'io', 'me', 'tv', 'cc', 'ws', 'link', 'click', 'xyz', 'online', 'site',
  'store', 'shop', 'app', 'dev', 'live', 'top', 'club', 'vip', 'icu', 'pw', 'tr',
].sort((a, b) => b.length - a.length);

const TLD_ALTERNATION = TLDS.map((t) => t.replace('.', '\\.')).join('|');

// Türkçe harfler — domain etiketlerinde (TLD'lerin KENDİSİNDE değil, TLD'den
// önceki etiketlerde, ör. "örnek" içinde "örnek.com.tr") geçebilir. Bunlar
// olmadan hem [a-zA-Z0-9] karakter sınıfı bu harfleri atlıyordu (eşleşen
// metnin baş harfi kayboluyordu — bkz. findUrlInText testleri) hem de `\b`
// sınırı JS'te SADECE ASCII \w'ye göre çalıştığı için "ö" gibi bir harften
// hemen önceki boşluk/harf geçişini "sınır" saymıyordu. Bu yüzden aşağıda
// klasik `\b` yerine, bu harfleri de "kelime karakteri" sayan özel bir
// negative-lookbehind sınırı kullanılıyor (sadece çıplak alan adı deseninin
// BAŞINDA — TLD'ler zaten sadece ASCII olduğundan sondaki `\b` değişmedi).
const TR_CHARS = 'çÇğĞıİöÖşŞüÜ';

// Tek bir birleşik regex — üç alternatif, leftmost-alternative-wins ile
// metindeki İLK eşleşen deseni döner (öncelik: http(s):// > www. > çıplak
// alan adı — aynı konumda birden fazla desen uyduğunda anlamlı olan budur).
const COMBINED_URL_RE = new RegExp(
  '(https?://\\S+)' +
    `|(\\bwww\\.[a-zA-Z0-9${TR_CHARS}-]+(?:\\.[a-zA-Z0-9${TR_CHARS}-]+)*\\b(?:/\\S*)?)` +
    `|((?<![a-zA-Z0-9_${TR_CHARS}])(?:[a-zA-Z0-9${TR_CHARS}](?:[a-zA-Z0-9${TR_CHARS}-]{0,61}[a-zA-Z0-9${TR_CHARS}])?\\.)+(?:${TLD_ALTERNATION})\\b(?:/\\S*)?)`,
  'i'
);

/**
 * Metinde bir URL/bağlantı deseni bulursa eşleşen alt-dizeyi (ör.
 * "http://evil.com", "evil.com"), bulamazsa null döner. Sadece görüntüleme
 * amaçlı — admin'e "bu tetikledi" göstermek için kullanılır.
 */
export function findUrlInText(text) {
  if (!text) return null;
  const match = COMBINED_URL_RE.exec(text);
  return match ? match[0] : null;
}

/** findUrlInText(text) !== null kısayolu — koşullu render/gate için. */
export function hasUrlInText(text) {
  return findUrlInText(text) !== null;
}
